import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { hasPermission } from '../../utils/permissions';
import { PERMISSIONS } from '../../constants/roles';
import { getGoogleAccessToken, getAuthToken } from '../../utils/session';

const BRAND_BLUE = '#4841F0';

const PROXY_URL = process.env.NODE_ENV === 'production'
  ? '/.netlify/functions/proxy'
  : 'http://localhost:3001/api/proxy';

const sheetGet = async (endpoint) => {
  const res = await fetch(`${PROXY_URL}?endpoint=${endpoint}`);
  const json = await res.json();
  return Array.isArray(json.data) ? json.data : [];
};

const sheetPost = async (endpoint, data) => {
  const res = await fetch(PROXY_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ endpoint, data })
  });
  return res.json();
};

const ClientAccessPanel = () => {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showAddClient, setShowAddClient] = useState(false);
  const [newClient, setNewClient] = useState({ email: '', clientName: '' });
  const [addingClient, setAddingClient] = useState(false);
  const [sendingLinkFor, setSendingLinkFor] = useState(null);
  const [generatedLink, setGeneratedLink] = useState(null);

  useEffect(() => {
    sheetGet('clients')
      .then(rows => setClients(rows.filter(r => r.email)))
      .catch(() => setClients([]))
      .finally(() => setLoading(false));
  }, []);  

  // Check permission
  if (!hasPermission(user, PERMISSIONS.MANAGE_CLIENT_ACCESS) && !hasPermission(user, PERMISSIONS.MANAGE_USERS)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-6xl mb-4">🚫</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to manage client access.</p>
        </div>
      </div>
    );
  }

  const clearMessages = () => {
    setTimeout(() => {
      setError(null);
      setSuccess(null);
    }, 5000);
  };

  const handleAddClient = async (e) => {
    e.preventDefault();
    if (!newClient.email || !newClient.clientName) {
      setError('Please fill in both email and client name.');
      clearMessages();
      return;
    }

    // Check for duplicate
    const existing = clients.find(c => c.email.toLowerCase() === newClient.email.toLowerCase());
    if (existing) {
      setError('A client with this email already exists.');
      clearMessages();
      return;
    }

    const clientEntry = {
      email: newClient.email,
      clientName: newClient.clientName,
      status: 'active',
      createdAt: new Date().toISOString()
    };

    setAddingClient(true);
    try {
      await sheetPost('upsertClient', clientEntry);
      setClients(prev => [...prev, clientEntry]);
      setNewClient({ email: '', clientName: '' });
      setShowAddClient(false);
      setSuccess(`Client "${newClient.clientName}" added successfully. Send them a magic link to grant access.`);
      clearMessages();
    } catch (err) {
      setError('Failed to save client to sheet. Please try again.');
      clearMessages();
    } finally {
      setAddingClient(false);
    }
  };

  const handleSendLink = async (clientEmail, clientName, isResend = false) => {
    setSendingLinkFor(clientEmail);
    setError(null);
    setGeneratedLink(null);

    try {
      const isProduction = process.env.NODE_ENV === 'production';
      const apiUrl = isProduction
        ? '/.netlify/functions/magic-link'
        : 'http://localhost:3001/api/magic-link';

      const adminToken = getGoogleAccessToken() || getAuthToken();
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(adminToken ? { 'Authorization': `Bearer ${adminToken}` } : {})
        },
        body: JSON.stringify({
          action: isResend ? 'resend' : 'generate',
          clientEmail,
          clientName,
          expiresInDays: 7
        })
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to generate magic link');
      }

      const portalLink = result.data.magicLink || result.data.email?.link;
      if (!portalLink) {
        throw new Error('Magic link was created, but no portal URL was returned.');
      }

      // Update last link sent timestamp in sheet and local state
      const now = new Date().toISOString();
      const target = clients.find(c => c.email === clientEmail);
      const updatedEntry = { ...target, lastLinkSent: now, portalLink, portalLinkCreatedAt: now };
      await sheetPost('upsertClient', updatedEntry);
      setClients(prev => prev.map(c => c.email === clientEmail ? updatedEntry : c));

      const emailSent = result.data.email?.sent;
      const emailReason = result.data.email?.reason;
      setGeneratedLink(portalLink);

      if (emailSent) {
        setSuccess(`Magic link ${isResend ? 're-sent' : 'sent'} to ${clientEmail} via email.`);
      } else if (emailReason === 'not_configured') {
        setSuccess('Email sending is not configured in this environment yet. The portal link is ready below for manual testing.');
      } else if (emailReason === 'send_failed') {
        setSuccess(`Email delivery failed for ${clientEmail}. The portal link is ready below for manual testing.`);
      } else {
        setSuccess('Magic link generated. Copy or open the portal link below.');
      }
      clearMessages();
    } catch (err) {
      console.error('Error sending magic link:', err);
      setError(err.message || 'Failed to send magic link. Please try again.');
      clearMessages();
    } finally {
      setSendingLinkFor(null);
    }
  };

  const handleRevokeAccess = async (clientEmail) => {
    if (!window.confirm(`Revoke access for ${clientEmail}? Their existing links will no longer work.`)) {
      return;
    }
    try {
      await sheetPost('revokeClient', { email: clientEmail });
      setClients(prev => prev.map(c => c.email === clientEmail ? { ...c, status: 'revoked' } : c));
      setSuccess(`Access revoked for ${clientEmail}.`);
      clearMessages();
    } catch {
      setError('Failed to revoke access. Please try again.');
      clearMessages();
    }
  };

  const handleReactivate = async (clientEmail) => {
    try {
      const target = clients.find(c => c.email === clientEmail);
      await sheetPost('upsertClient', { ...target, status: 'active' });
      setClients(prev => prev.map(c => c.email === clientEmail ? { ...c, status: 'active' } : c));
      setSuccess(`Access reactivated for ${clientEmail}. Send a new magic link.`);
      clearMessages();
    } catch {
      setError('Failed to reactivate client. Please try again.');
      clearMessages();
    }
  };

  const handleDeleteClient = async (clientEmail) => {
    if (!window.confirm(`Permanently remove ${clientEmail} from the client list?`)) {
      return;
    }
    try {
      await sheetPost('revokeClient', { email: clientEmail });
      setClients(prev => prev.filter(c => c.email !== clientEmail));
      setSuccess('Client removed.');
      clearMessages();
    } catch {
      setError('Failed to remove client. Please try again.');
      clearMessages();
    }
  };

  const copyLink = (link) => {
    navigator.clipboard.writeText(link).then(() => {
      setSuccess('Link copied to clipboard!');
      clearMessages();
    });
  };

  const openLink = (link) => {
    window.open(link, '_blank', 'noopener,noreferrer');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const activeClients = clients.filter(c => c.status === 'active');
  const revokedClients = clients.filter(c => c.status === 'revoked');

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 mx-auto mb-4" style={{ borderColor: BRAND_BLUE }}></div>
          <p className="text-gray-500 text-sm">Loading clients...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-5xl">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-3 mb-2">
          <div className="p-2 rounded-lg" style={{ background: `${BRAND_BLUE}12` }}>
            <svg className="w-6 h-6" style={{ color: BRAND_BLUE }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Client Portal Access</h1>
            <p className="text-sm text-gray-500">Manage private portal access for your clients</p>
          </div>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-center">
          <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 rounded-lg text-sm flex items-center">
          <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {success}
        </div>
      )}

      {/* Generated Link Display */}
      {generatedLink && (
        <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-blue-800">Magic Link Generated</p>
            <button
              onClick={() => setGeneratedLink(null)}
              className="text-blue-400 hover:text-blue-600 text-xs"
            >
              Dismiss
            </button>
          </div>
          <div className="flex items-center space-x-2">
            <input
              type="text"
              readOnly
              value={generatedLink}
              className="flex-1 text-xs bg-white border border-blue-200 rounded px-3 py-2 text-gray-600 font-mono"
            />
            <div className="flex items-center space-x-2 flex-shrink-0">
              <button
                onClick={() => copyLink(generatedLink)}
                className="px-3 py-2 bg-blue-600 text-white text-xs font-medium rounded hover:bg-blue-700 transition-colors"
              >
                Copy
              </button>
              <button
                onClick={() => openLink(generatedLink)}
                className="px-3 py-2 bg-white border border-blue-200 text-blue-700 text-xs font-medium rounded hover:bg-blue-100 transition-colors"
              >
                Open
              </button>
            </div>
          </div>
          <p className="text-xs text-blue-500 mt-2">Share this link with the client. It expires in 7 days.</p>
        </div>
      )}

      {/* Add Client Button */}
      <div className="mb-6">
        <button
          onClick={() => setShowAddClient(!showAddClient)}
          className="text-white px-4 py-2.5 rounded-lg font-medium text-sm transition-colors"
          style={{ background: BRAND_BLUE }}
        >
          {showAddClient ? 'Cancel' : '+ Add Client'}
        </button>

        {showAddClient && (
          <div className="mt-4 bg-gray-50 border border-gray-200 p-5 rounded-xl">
            <h3 className="text-sm font-semibold text-gray-800 mb-3">Register New Client</h3>
            <form onSubmit={handleAddClient} className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Client Name</label>
                  <input
                    type="text"
                    value={newClient.clientName}
                    onChange={(e) => setNewClient({ ...newClient, clientName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g. Voize, TechCorp"
                    required
                  />
                  <p className="text-xs text-gray-400 mt-1">Must match the client name in your recruitment data</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Contact Email</label>
                  <input
                    type="email"
                    value={newClient.email}
                    onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="client@company.com"
                    required
                  />
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  type="submit"
                  disabled={addingClient}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center"
                >
                  {addingClient ? (
                    <>
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-2"></div>
                      Saving...
                    </>
                  ) : 'Add Client'}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowAddClient(false); setNewClient({ email: '', clientName: '' }); }}
                  className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}
      </div>

      {/* Active Clients Table */}
      <div className="bg-white shadow-sm rounded-xl border border-gray-100 overflow-hidden mb-6">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-800">
            Active Clients ({activeClients.length})
          </h2>
        </div>

        {activeClients.length === 0 ? (
          <div className="px-5 py-10 text-center text-gray-400 text-sm">
            No clients registered yet. Add your first client above.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Link Sent</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Added</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Portal</th>
                  <th className="px-5 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {activeClients.map((client) => (
                  <tr key={client.email} className="hover:bg-gray-50 transition-colors">
                    <td className="px-5 py-3.5">
                      <p className="text-sm font-medium text-gray-900">{client.clientName}</p>
                    </td>
                    <td className="px-5 py-3.5">
                      <p className="text-sm text-gray-600">{client.email}</p>
                    </td>
                    <td className="px-5 py-3.5">
                      <p className="text-sm text-gray-500">{formatDate(client.lastLinkSent)}</p>
                    </td>
                    <td className="px-5 py-3.5">
                      <p className="text-sm text-gray-500">{formatDate(client.createdAt)}</p>
                    </td>
                    <td className="px-5 py-3.5">
                      {client.portalLink ? (
                        <div className="flex flex-col items-start space-y-2">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => openLink(client.portalLink)}
                              className="px-3 py-1.5 text-xs font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors"
                            >
                              Open Portal
                            </button>
                            <button
                              onClick={() => copyLink(client.portalLink)}
                              className="px-3 py-1.5 text-xs font-medium rounded-md text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
                            >
                              Copy Link
                            </button>
                          </div>
                          <p className="text-xs text-gray-400">Generated {formatDate(client.portalLinkCreatedAt)}</p>
                        </div>
                      ) : (
                        <p className="text-xs text-gray-400">Generate a link to preview the portal</p>
                      )}
                    </td>
                    <td className="px-5 py-3.5 text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleSendLink(client.email, client.clientName, false)}
                          disabled={sendingLinkFor === client.email}
                          className="px-3 py-1.5 text-xs font-medium rounded-md text-white transition-colors disabled:opacity-50"
                          style={{ background: BRAND_BLUE }}
                          title="Send magic link"
                        >
                          {sendingLinkFor === client.email ? (
                            <span className="flex items-center">
                              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
                              Sending...
                            </span>
                          ) : client.lastLinkSent ? 'Resend Link' : 'Send Link'}
                        </button>
                        <button
                          onClick={() => handleRevokeAccess(client.email)}
                          className="px-3 py-1.5 text-xs font-medium rounded-md text-red-600 bg-red-50 hover:bg-red-100 transition-colors"
                          title="Revoke access"
                        >
                          Revoke
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Revoked Clients */}
      {revokedClients.length > 0 && (
        <div className="bg-white shadow-sm rounded-xl border border-gray-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100">
            <h2 className="text-base font-semibold text-gray-500">
              Revoked ({revokedClients.length})
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Revoked</th>
                  <th className="px-5 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {revokedClients.map((client) => (
                  <tr key={client.email} className="hover:bg-gray-50 opacity-60">
                    <td className="px-5 py-3">
                      <p className="text-sm text-gray-600">{client.clientName}</p>
                    </td>
                    <td className="px-5 py-3">
                      <p className="text-sm text-gray-500">{client.email}</p>
                    </td>
                    <td className="px-5 py-3">
                      <p className="text-sm text-gray-500">{formatDate(client.revokedAt)}</p>
                    </td>
                    <td className="px-5 py-3 text-right space-x-2">
                      <button
                        onClick={() => handleReactivate(client.email)}
                        className="px-3 py-1.5 text-xs font-medium rounded-md text-emerald-600 bg-emerald-50 hover:bg-emerald-100 transition-colors"
                      >
                        Reactivate
                      </button>
                      <button
                        onClick={() => handleDeleteClient(client.email)}
                        className="px-3 py-1.5 text-xs font-medium rounded-md text-gray-500 bg-gray-100 hover:bg-gray-200 transition-colors"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="mt-8 bg-blue-50 border border-blue-100 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-blue-900 mb-2">How Client Portal Access Works</h3>
        <div className="grid sm:grid-cols-2 gap-4 text-xs text-blue-700">
          <div className="space-y-2">
            <p><strong>1. Add client</strong> — Register their email and the client name as it appears in your recruitment data.</p>
            <p><strong>2. Send magic link</strong> — They receive a secure, branded email with a one-click link to their portal.</p>
          </div>
          <div className="space-y-2">
            <p><strong>3. Client views data</strong> — They see only their own recruitment data: pipeline, roles, metrics, and satisfaction scores.</p>
            <p><strong>4. Revoke anytime</strong> — Instantly revoke access. No existing link will work.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientAccessPanel;
